import { ClerkProvider as ClerkProviderBase } from '@clerk/clerk-react';
import type { ReactNode } from 'react';
import { clerkAppearance } from './clerkAppearance';

// Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Navigation paths for Clerk
const clerkPaths = {
  signIn: '/sign-in',
  signUp: '/sign-up',
  afterSignIn: '/dashboard',  // fallback redirect after sign-in
  afterSignUp: '/get-started',  // fallback redirect after sign-up
} as const;

interface ClerkProviderProps {
  children: ReactNode;
}

/**
 * Clerk authentication provider wrapper
 * Handles authentication state and provides auth context
 */
export function ClerkProvider({ children }: ClerkProviderProps) {
  if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
  }
  return (
    <ClerkProviderBase
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl={clerkPaths.signIn}
      signUpUrl={clerkPaths.signUp}
      signInFallbackRedirectUrl={clerkPaths.afterSignIn}
      signUpFallbackRedirectUrl={clerkPaths.afterSignUp}
      appearance={clerkAppearance}
    >
      {children}
    </ClerkProviderBase>
  );
}

// Re-export commonly used Clerk hooks and components
export {
  useAuth,
  useUser,
  useClerk,
  useSession,
  useSignIn,
  useSignUp,
  SignIn,
  SignUp,
  SignedIn,
  SignedOut,
  UserButton,
  SignInButton,
  SignUpButton,
  SignOutButton,
  RedirectToSignIn,
} from '@clerk/clerk-react';

// Type exports
export type { UserResource, SessionResource } from '@clerk/types';
